import React, { useEffect, useRef, useState } from 'react';
import { Crown, Gem, Star, Sparkles } from 'lucide-react';

function giftConfig(amount) {
  if (amount >= 10) return { Icon: Crown, color: 'text-[#d4a853]', bg: 'bg-[#d4a853]/15 border-[#d4a853]/30', glow: 'shadow-[0_0_40px_rgba(212,168,83,0.3)]', size: 28, label: 'Crown', duration: 7000, isHonor: true };
  if (amount >= 5) return { Icon: Gem, color: 'text-[#d4a853]', bg: 'bg-[#d4a853]/10 border-[#d4a853]/20', glow: 'shadow-[0_0_24px_rgba(212,168,83,0.2)]', size: 22, label: 'Diamond', duration: 5500, isHonor: false };
  return { Icon: Star, color: 'text-white/70', bg: 'bg-white/8 border-white/12', glow: '', size: 18, label: 'Star', duration: 4000, isHonor: false };
}

/* ═══════════════════════════════════════════════════════════════════════
   MOMENTO DE HONOR — Cinematic fullscreen for Crown (10€+) tips
   3-second dramatic reveal with particle effects and glory animation
═══════════════════════════════════════════════════════════════════════ */
const HonorMoment = ({ gift, onComplete }) => {
  const [phase, setPhase] = useState('enter'); // enter | glory | exit

  useEffect(() => {
    // Phase 1: Enter (0.8s)
    const gloryTimer = setTimeout(() => setPhase('glory'), 800);
    // Phase 2: Glory hold (2s)
    const exitTimer = setTimeout(() => setPhase('exit'), 2800);
    // Phase 3: Exit and complete (3.3s total)
    const completeTimer = setTimeout(onComplete, 3300);
    
    return () => {
      clearTimeout(gloryTimer);
      clearTimeout(exitTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  return (
    <div 
      data-testid="pulse-live-honor-moment"
      className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none overflow-hidden"
      style={{ 
        background: phase === 'exit' 
          ? 'rgba(0,0,0,0)' 
          : 'radial-gradient(ellipse at center, rgba(212,168,83,0.15) 0%, rgba(0,0,0,0.95) 70%)',
        transition: 'background 0.5s ease-out'
      }}
    >
      {/* Particle rain effect */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#d4a853] rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: '-10px',
              opacity: 0.6 + Math.random() * 0.4,
              animation: `honorParticle ${2 + Math.random()}s linear infinite`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      {/* Radial glow burst */}
      <div 
        className="absolute w-[600px] h-[600px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(212,168,83,0.3) 0%, transparent 70%)',
          animation: phase === 'glory' ? 'honorGlow 1s ease-out forwards' : 'none',
          opacity: phase === 'exit' ? 0 : 1,
          transition: 'opacity 0.5s ease-out',
        }}
      />

      {/* Main content */}
      <div 
        className="relative flex flex-col items-center gap-6"
        style={{
          animation: phase === 'enter' ? 'honorEnter 0.8s cubic-bezier(0.16,1,0.3,1) forwards' 
                   : phase === 'exit' ? 'honorExit 0.5s ease-in forwards' 
                   : 'honorFloat 2s ease-in-out infinite',
        }}
      >
        {/* Crown icon with rotation */}
        <div 
          className="relative"
          style={{ animation: 'honorCrownSpin 3s ease-in-out' }}
        >
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#d4a853] to-[#8b6914] flex items-center justify-center shadow-[0_0_80px_rgba(212,168,83,0.6)]">
            <Crown size={48} className="text-black" />
          </div>
          {/* Sparkle orbits */}
          <Sparkles 
            size={20} 
            className="absolute -top-2 -right-2 text-[#d4a853]" 
            style={{ animation: 'honorSparkle 0.5s ease infinite' }}
          />
          <Sparkles 
            size={16} 
            className="absolute -bottom-1 -left-3 text-[#d4a853]" 
            style={{ animation: 'honorSparkle 0.5s ease infinite 0.25s' }}
          />
        </div>

        {/* Text reveal */}
        <div className="text-center">
          <p 
            className="text-[#d4a853] text-xs uppercase tracking-[0.3em] mb-2 font-medium"
            style={{ animation: 'honorTextFade 0.6s ease forwards 0.3s', opacity: 0 }}
          >
            Momento de Honor
          </p>
          <h2 
            className="text-white text-3xl md:text-4xl font-bold mb-3"
            style={{ animation: 'honorNameReveal 0.8s cubic-bezier(0.16,1,0.3,1) forwards 0.4s', opacity: 0, transform: 'translateY(20px)' }}
          >
            {gift.user_name}
          </h2>
          <div 
            className="flex items-center justify-center gap-2"
            style={{ animation: 'honorTextFade 0.6s ease forwards 0.6s', opacity: 0 }}
          >
            <Crown size={18} className="text-[#d4a853]" />
            <span className="text-[#d4a853] text-xl font-bold">{gift.amount}€</span>
          </div>
          {gift.message && (
            <p 
              className="text-white/70 text-sm mt-4 max-w-xs mx-auto italic"
              style={{ animation: 'honorTextFade 0.6s ease forwards 0.8s', opacity: 0 }}
            >
              "{gift.message}"
            </p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes honorParticle {
          0% { transform: translateY(0) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 0.5; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes honorGlow {
          0% { transform: scale(0.5); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: scale(1.5); opacity: 0.3; }
        }
        @keyframes honorEnter {
          0% { opacity: 0; transform: scale(0.3) translateY(50px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes honorFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes honorExit {
          0% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.2); }
        }
        @keyframes honorCrownSpin {
          0% { transform: rotateY(0deg); }
          25% { transform: rotateY(15deg); }
          75% { transform: rotateY(-15deg); }
          100% { transform: rotateY(0deg); }
        }
        @keyframes honorSparkle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
        @keyframes honorTextFade {
          to { opacity: 1; }
        }
        @keyframes honorNameReveal {
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
   Standard Gift Overlay — For Star and Diamond tiers
═══════════════════════════════════════════════════════════════════════ */
const StandardGiftCard = ({ gift }) => {
  const { Icon, color, bg, glow, size, amount, user_name, message } = gift;
  
  return (
    <div data-testid="pulse-live-gift-overlay" className="absolute inset-x-0 top-4 z-30 flex justify-center pointer-events-none" style={{ animation: 'giftEnter .5s cubic-bezier(.16,1,.3,1)' }}>
      <div className={`rounded-2xl border ${bg} ${glow} backdrop-blur-lg px-5 py-3.5 flex items-center gap-3 max-w-md`}>
        <div className={`w-12 h-12 rounded-full bg-black/30 border border-white/10 flex items-center justify-center ${color}`} style={{ animation: 'giftPulse 1.5s ease infinite' }}>
          <Icon size={size} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-bold text-white">{user_name}</span>
            <span className={`text-[11px] font-semibold ${color}`}>{amount}{'\u20AC'}</span>
          </div>
          {message && <p className="text-[12px] text-white/60 truncate max-w-[250px]">{message}</p>}
        </div>
      </div>

      <style>{`
        @keyframes giftEnter {
          from { opacity:0; transform:translateY(-30px) scale(.85); }
          to { opacity:1; transform:translateY(0) scale(1); }
        }
        @keyframes giftPulse {
          0%,100% { transform:scale(1); }
          50% { transform:scale(1.12); }
        }
      `}</style>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
   Main Export — Orchestrates gifts with honor moments
═══════════════════════════════════════════════════════════════════════ */
export const LiveGiftOverlay = ({ session, lang }) => {
  const [activeGift, setActiveGift] = useState(null);
  const [honorMoment, setHonorMoment] = useState(null);
  const seenIds = useRef(new Set());
  const queue = useRef([]);
  const processing = useRef(false);

  useEffect(() => {
    const gifts = session?.gifts || [];
    const newGifts = gifts.filter(g => g.status === 'approved' && !seenIds.current.has(g.id));
    newGifts.forEach(g => {
      seenIds.current.add(g.id);
      queue.current.push(g);
    });
    processQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.gifts]);

  const processQueue = () => {
    if (processing.current || queue.current.length === 0) return;
    processing.current = true;
    const gift = queue.current.shift();
    const cfg = giftConfig(gift.amount);
    const giftData = { ...gift, ...cfg };

    // TTS for all gifts
    if (gift.message && 'speechSynthesis' in window) {
      const text = `${gift.user_name}: ${gift.message}`;
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = lang === 'es' ? 'es-ES' : 'en-US';
      utter.rate = 0.95;
      utter.pitch = 1.0;
      utter.volume = 0.9;
      window.speechSynthesis.speak(utter);
    }

    // Crown (10€+) = Honor Moment
    if (cfg.isHonor) {
      setHonorMoment(giftData);
    } else {
      // Star/Diamond = Standard overlay
      setActiveGift(giftData);
      setTimeout(() => {
        setActiveGift(null);
        processing.current = false;
        processQueue();
      }, cfg.duration);
    }
  };

  const handleHonorComplete = () => {
    setHonorMoment(null);
    processing.current = false;
    processQueue();
  };

  return (
    <>
      {honorMoment && (
        <HonorMoment gift={honorMoment} onComplete={handleHonorComplete} />
      )}
      {activeGift && !honorMoment && (
        <StandardGiftCard gift={activeGift} />
      )}
    </>
  );
};
